import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Paragraph from "@tiptap/extension-paragraph";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { useEffect, useRef, useState, useMemo } from "react";
import { toast } from "sonner";
import InstagramEmbed from "@/components/InstagramEmbed";
import Video from "@/components/VideoNode";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { analyzeText } from "@/lib/writingHelper";

// Paragraph node extended with an "indent" attribute, toggled from the toolbar,
// so authors can indent (or un-indent) any paragraph in a journal entry.
const IndentParagraph = Paragraph.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      indent: {
        default: false,
        parseHTML: (element) => element.getAttribute("data-indent") === "true",
        renderHTML: (attributes) => (attributes.indent ? { "data-indent": "true" } : {}),
      },
    };
  },
});

// Curated palette matching the site's white + gold aesthetic — kept tight so
// authors don't create a rainbow of clashing shades in journal entries.
const COLOR_SWATCHES = [
  { name: "Default", value: null },
  { name: "Gold", value: "#C5A059" },
  { name: "Deep gold", value: "#A68545" },
  { name: "Ink", value: "#1A1F24" },
  { name: "Slate", value: "#5C6670" },
  { name: "Sand", value: "#B8956B" },
  { name: "Sea", value: "#6F8E9A" },
  { name: "Rose", value: "#B87070" },
  { name: "Sage", value: "#7A9077" },
];

const btnCls = (active) =>
  `px-3 py-1.5 text-xs tracking-[0.15em] uppercase border ${
    active ? "bg-[#C5A059] text-white border-[#C5A059]" : "bg-white text-[#1A1F24] border-[#E5E5E5] hover:border-[#C5A059]"
  } transition-colors`;

const toAbs = (url) => {
  if (!url) return url;
  if (url.startsWith("http")) return url;
  if (url.startsWith("/api/")) return `${API_BASE}${url.slice(4)}`;
  return url;
};

export default function RichEditor({ value, onChange, placeholder = "Begin the entry…" }) {
  const imgFileRef = useRef(null);
  const videoFileRef = useRef(null);
  const [uploading, setUploading] = useState(null); // null | "image" | "video"
  const [colorOpen, setColorOpen] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] }, link: false, paragraph: false }),
      IndentParagraph,
      TextStyle,
      Color.configure({ types: ["textStyle"] }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank", class: "gv-link text-[#C5A059]" },
      }),
      Image.configure({ inline: false, allowBase64: false, HTMLAttributes: { class: "gv-editor-image" } }),
      Video,
      InstagramEmbed,
    ],
    content: value || "",
    editorProps: {
      attributes: {
        "data-testid": "rich-editor-area",
        spellcheck: "true",
        autocorrect: "on",
        autocapitalize: "sentences",
        class:
          "prose prose-stone max-w-none min-h-[260px] focus:outline-none text-[#1A1F24] leading-relaxed text-base font-light",
      },
    },
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  useEffect(() => {
    if (!editor) return;
    if (value !== editor.getHTML()) editor.commands.setContent(value || "", false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const analysis = useMemo(() => analyzeText(editor?.getText() || ""), [editor, value]);

  if (!editor) return null;

  const addLink = () => {
    const url = window.prompt("URL");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const insertImageByUrl = (url) => {
    editor
      .chain()
      .focus()
      .insertContent([
        { type: "image", attrs: { src: url, alt: "" } },
        { type: "paragraph" },
      ])
      .run();
  };

  const insertVideoByUrl = (url) => {
    editor.chain().focus().setVideo({ src: url }).run();
  };

  const openFilePicker = (kind) => {
    if (kind === "image") imgFileRef.current?.click();
    else videoFileRef.current?.click();
  };

  const uploadFile = async (kind, file) => {
    if (!file) return;
    const endpoint = kind === "image" ? "/uploads/image" : "/uploads/video";
    const limit = kind === "image" ? 5 : 50;
    if (file.size > limit * 1024 * 1024) {
      toast.error(`${kind} too large. Limit is ${limit} MB.`);
      return;
    }
    setUploading(kind);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post(endpoint, form, { headers: { "Content-Type": "multipart/form-data" } });
      const abs = toAbs(data.url);
      if (kind === "image") insertImageByUrl(abs);
      else insertVideoByUrl(abs);
      toast.success(`${kind === "image" ? "Image" : "Video"} uploaded`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Upload failed."));
    } finally {
      setUploading(null);
    }
  };

  const addInstagram = () => {
    const url = window.prompt("Paste an Instagram reel / post URL");
    if (!url) return;
    editor.chain().focus().setInstagramEmbed(url).run();
  };

  const toggleIndent = () => {
    const isIndented = editor.isActive("paragraph", { indent: true });
    editor.chain().focus().updateAttributes("paragraph", { indent: !isIndented }).run();
  };

  const insertParagraphBreak = () => {
    editor.chain().focus().splitBlock().run();
  };

  return (
    <div className="border border-[#E5E5E5] bg-white">
      {/* Toolbar */}
      <div className="flex flex-wrap gap-2 p-3 border-b border-[#E5E5E5] bg-[#FAFAFA]">
        <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={btnCls(editor.isActive("heading", { level: 2 }))} data-testid="rt-h2">H2</button>
        <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} className={btnCls(editor.isActive("heading", { level: 3 }))} data-testid="rt-h3">H3</button>
        <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={btnCls(editor.isActive("bold"))} data-testid="rt-bold">Bold</button>
        <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={btnCls(editor.isActive("italic"))} data-testid="rt-italic">Italic</button>

        {/* Font colour picker — small popover with a curated site-tone palette */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setColorOpen((v) => !v)}
            className={btnCls(!!editor.getAttributes("textStyle").color)}
            data-testid="rt-color-toggle"
            title="Font colour"
          >
            <span className="inline-flex items-center gap-1.5">
              <span
                className="inline-block w-3 h-3 rounded-full border border-[#E5E5E5]"
                style={{ background: editor.getAttributes("textStyle").color || "#1A1F24" }}
              />
              Colour
            </span>
          </button>
          {colorOpen && (
            <div
              className="absolute z-20 top-full left-0 mt-1 bg-white border border-[#E5E5E5] shadow-lg p-2 flex flex-wrap gap-1 w-[168px]"
              data-testid="rt-color-palette"
            >
              {COLOR_SWATCHES.map((sw) => (
                <button
                  key={sw.name}
                  type="button"
                  title={sw.name}
                  data-testid={`rt-color-${sw.name.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => {
                    if (sw.value) {
                      editor.chain().focus().setColor(sw.value).run();
                    } else {
                      editor.chain().focus().unsetColor().run();
                    }
                    setColorOpen(false);
                  }}
                  className="w-7 h-7 border border-[#E5E5E5] hover:border-[#C5A059] transition-colors flex items-center justify-center"
                  style={{ background: sw.value || "#FFFFFF" }}
                >
                  {!sw.value && <span className="text-[10px] text-[#5C6670]">×</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        <button type="button" onClick={() => editor.chain().focus().toggleBlockquote().run()} className={btnCls(editor.isActive("blockquote"))} data-testid="rt-quote">“ Quote</button>
        <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()} className={btnCls(editor.isActive("bulletList"))} data-testid="rt-ul">• List</button>
        <button type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()} className={btnCls(editor.isActive("orderedList"))} data-testid="rt-ol">1. List</button>
        <button type="button" onClick={addLink} className={btnCls(editor.isActive("link"))} data-testid="rt-link">Link</button>
        <button
          type="button"
          onClick={insertParagraphBreak}
          className={btnCls(false)}
          data-testid="rt-paragraph-break"
          title="Start a new paragraph at the cursor"
        >
          ¶ New Paragraph
        </button>
        <button
          type="button"
          onClick={toggleIndent}
          className={btnCls(editor.isActive("paragraph", { indent: true }))}
          data-testid="rt-indent"
          title="Indent this paragraph, letter-style"
        >
          ⇥ Indent
        </button>
        <button type="button" onClick={() => openFilePicker("image")} className={btnCls(false)} data-testid="rt-image" disabled={uploading === "image"}>
          {uploading === "image" ? "Uploading…" : "Image / GIF"}
        </button>
        <button type="button" onClick={() => openFilePicker("video")} className={btnCls(false)} data-testid="rt-video" disabled={uploading === "video"}>
          {uploading === "video" ? "Uploading…" : "Video"}
        </button>
        <button type="button" onClick={addInstagram} className={btnCls(false)} data-testid="rt-instagram">Instagram Reel</button>
        <button type="button" onClick={() => editor.chain().focus().setHorizontalRule().run()} className={btnCls(false)} data-testid="rt-hr">— Rule</button>

        <input type="file" ref={imgFileRef} className="hidden" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml" onChange={(e) => uploadFile("image", e.target.files?.[0])} data-testid="rt-image-file" />
        <input type="file" ref={videoFileRef} className="hidden" accept="video/mp4,video/webm,video/quicktime,video/ogg" onChange={(e) => uploadFile("video", e.target.files?.[0])} data-testid="rt-video-file" />
      </div>

      {/* Editor surface */}
      <div className="px-6 py-6">
        <EditorContent editor={editor} />
        {!editor.getText() && (
          <div className="pointer-events-none -mt-[1.8rem] text-[#5C6670] font-light italic select-none">{placeholder}</div>
        )}
      </div>

      {/* Writing helper */}
      <div className="border-t border-[#E5E5E5] bg-[#FAFAFA] px-5 py-3" data-testid="writing-helper">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-[0.7rem] tracking-[0.18em] uppercase text-[#5C6670]">
          <span data-testid="wh-words"><strong className="text-[#1A1F24]">{analysis.wordCount}</strong> words</span>
          <span data-testid="wh-chars"><strong className="text-[#1A1F24]">{analysis.charCount}</strong> chars</span>
          <span data-testid="wh-sentences"><strong className="text-[#1A1F24]">{analysis.sentenceCount}</strong> sentences</span>
          <span data-testid="wh-avg"><strong className="text-[#1A1F24]">{analysis.avgWordsPerSentence}</strong> avg w/sent</span>
          <span data-testid="wh-read"><strong className="text-[#1A1F24]">~{analysis.readingMinutes} min</strong> read</span>
          <span className="ml-auto text-[#C5A059]">Browser spellcheck on</span>
        </div>
        {analysis.issues.length > 0 && (
          <ul className="mt-3 space-y-1" data-testid="wh-issues">
            {analysis.issues.map((iss) => (
              <li
                key={`${iss.severity}-${iss.message}`}
                className={`text-xs flex items-start gap-2 ${iss.severity === "warn" ? "text-[#A68545]" : "text-[#5C6670]"}`}
              >
                <span aria-hidden="true">{iss.severity === "warn" ? "▲" : "·"}</span>
                <span>{iss.message}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
